import { useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { Layers } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import {
  GenericSortHeader,
  dealTableHeadRowClass,
  dealTableRowClass,
  useGenericTableSort,
} from '@/components/shared/DealTable'
import type { FigureTableColumn, FigureTableRow } from '@/types/dashboards'
import FigureValue, { formatFigure } from './FigureValue'

/** A cell the server actually measured. Missing, absent and empty all sort last. */
function hasValue(row: FigureTableRow, columnIndex: number): boolean {
  const cell = row.cells[columnIndex]
  return Boolean(cell) && cell.value !== null && cell.value !== undefined && cell.value !== ''
}

interface FigureTableProps {
  columns: FigureTableColumn[]
  rows: FigureTableRow[]
  /** Column index sorted on first render; null leaves server order intact. */
  defaultSortColumn?: number | null
  /** Show the trailing workspace column for rows that carry an href. */
  showWorkspaceLink?: boolean
  className?: string
}

/**
 * Dense table whose cells are Figures.
 *
 * Sorting reuses the platform's generic table primitives so a dashboard table
 * behaves exactly like the pipeline tables next to it. Absent values are held
 * out of the comparison and appended after it, so they sort LAST in both
 * directions — a sentinel cannot do that, because whichever extreme sorts last
 * descending sorts first ascending, and ascending is what the first click on a
 * column gives you.
 */
export default function FigureTable({
  columns,
  rows,
  defaultSortColumn = null,
  showWorkspaceLink = true,
  className,
}: FigureTableProps) {
  const navigate = useNavigate()

  const getValue = useCallback(
    (row: FigureTableRow, columnIndex: number): string | number => {
      const cell = row.cells[columnIndex]
      if (!hasValue(row, columnIndex)) return 0
      return typeof cell.value === 'number' ? cell.value : String(cell.value)
    },
    [],
  )

  const { sortColumn, sortDir, toggleSort, sortedRows } = useGenericTableSort(
    rows,
    getValue,
    defaultSortColumn,
  )

  // Array.prototype.sort is stable, so partitioning afterwards preserves the
  // order the comparator produced within each half.
  const orderedRows = useMemo(() => {
    if (sortColumn === null) return sortedRows
    const present = sortedRows.filter((row) => hasValue(row, sortColumn))
    const absent = sortedRows.filter((row) => !hasValue(row, sortColumn))
    return absent.length === 0 ? sortedRows : [...present, ...absent]
  }, [sortedRows, sortColumn])

  const hasWorkspace = showWorkspaceLink && rows.some((row) => row.href)

  return (
    <div className={clsx('min-h-0 overflow-x-auto', className)}>
      <table className="dense-table">
        <thead>
          <tr className={dealTableHeadRowClass()}>
            {columns.map((column, index) => (
              <GenericSortHeader
                key={column.key}
                label={column.label}
                columnIndex={index}
                sortColumn={sortColumn}
                sortDir={sortDir}
                onSort={toggleSort}
                align={column.align}
                className={column.width}
              />
            ))}
            {hasWorkspace && <th className="w-[36px]" aria-label="Open in workspace" />}
          </tr>
        </thead>
        <tbody>
          {orderedRows.map((row) => (
            <tr key={row.id} className={dealTableRowClass(false, 'cursor-default')}>
              {columns.map((column, index) => {
                const cell = row.cells[index]
                return (
                  <td
                    key={column.key}
                    className={clsx(
                      column.align === 'right'
                        ? 'text-right'
                        : column.align === 'center'
                          ? 'text-center'
                          : 'text-left',
                    )}
                    title={cell ? `${cell.label}: ${formatFigure(cell)}` : undefined /* exact on hover */}
                  >
                    {cell ? (
                      <FigureValue figure={cell} align={column.align} />
                    ) : (
                      <span className="text-stone-600">--</span>
                    )}
                  </td>
                )
              })}
              {hasWorkspace && (
                <td className="text-right">
                  {row.href && (
                    <button
                      type="button"
                      onClick={() => navigate(row.href as string)}
                      title="Open in workspace"
                      aria-label="Open in workspace"
                      className="rounded p-0.5 text-stone-600 transition-colors hover:text-amber-300"
                    >
                      <Layers size={11} />
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
